import classNames from 'classnames';
import { motion } from 'framer-motion';
import { cardUnits } from '../../../functions';
import { IHiddenCard, IVisibleCard } from '../../../Models/CardModels';
import { cardAriaLabel, CardSelection } from '../cardSelection';
import DraggableCard from '../DraggableCard/DraggableCard';
import { CARD_FACE_CLASS_NAME } from '../HandCards/HandCards';
import { TABLE_CARD_LAYOUT_TRANSITION } from '../TableCards/TableCards';

export interface ITableCardsInPlayProps {
  visibleCards: IVisibleCard[];
  hiddenCards: IHiddenCard[];
  selection: CardSelection;
}

const CARD_WIDTH = 84;
const BACK_OFFSET = 6;
const SLOT_WIDTH = CARD_WIDTH + BACK_OFFSET;
const MAX_SLOT_STEP = 110;
const SIDE_MARGIN = 10;

const CARD_CLASS_NAME = `${CARD_FACE_CLASS_NAME} w-[calc(84*var(--card-unit))] h-[calc(122*var(--card-unit))]`;

export default function TableCardsInPlay({
  visibleCards,
  hiddenCards,
  selection,
}: ITableCardsInPlayProps) {
  const isFaceDownPhase = visibleCards.length === 0;
  const {
    selectedIds,
    selectedCards,
    hasSelection,
    isPlayableWith,
    canToggle,
    isRejected,
    tapCard,
  } = selection;

  const slotCount = Math.max(visibleCards.length, hiddenCards.length);
  const middle = (slotCount - 1) / 2;
  const slotStep = `min(${cardUnits(MAX_SLOT_STEP)}, (100cqw - ${cardUnits(2 * SIDE_MARGIN + SLOT_WIDTH)}) / ${Math.max(slotCount - 1, 1)})`;
  const slots = Array.from({ length: slotCount }, (_, i) => i);

  const renderCard = (
    tableCard: IVisibleCard | IHiddenCard,
    offset: number,
    isPlayable: boolean
  ) => {
    const cardId = tableCard.card.cardId;
    const isSelected = selectedIds.includes(cardId);
    return (
      <motion.div
        key={cardId}
        layoutId={cardId}
        transition={TABLE_CARD_LAYOUT_TRANSITION}
        className="absolute flex"
        style={{ left: cardUnits(offset), top: cardUnits(offset) }}
      >
        <DraggableCard
          card={tableCard}
          cardClassName={classNames(CARD_CLASS_NAME, {
            'ring-3 ring-accent-fill': isSelected,
          })}
          dragGroup={isSelected ? selectedCards : undefined}
          disabled={!isPlayable}
          isRejected={isRejected(cardId)}
          ariaLabel={
            isPlayable
              ? cardAriaLabel(tableCard, isSelected, isPlayableWith([cardId]))
              : undefined
          }
          onClick={isPlayable ? () => tapCard(cardId) : undefined}
        />
      </motion.div>
    );
  };

  return (
    <section
      aria-label="Your table"
      className="@container flex h-[calc(212*var(--card-unit))] w-full shrink-0 items-center"
    >
      <div className="relative h-[calc(128*var(--card-unit))] w-full">
        {slots.map((i) => {
          const topCard = visibleCards[i] ?? hiddenCards[i];
          return (
            <div
              key={i}
              className={classNames(
                'absolute top-0 transition-[filter] duration-150',
                {
                  'brightness-60':
                    hasSelection && !canToggle(topCard.card.cardId),
                }
              )}
              style={{
                left: `calc(50% - ${cardUnits(SLOT_WIDTH / 2)} + ${i - middle} * ${slotStep})`,
              }}
            >
              {hiddenCards[i] &&
                renderCard(hiddenCards[i], BACK_OFFSET, isFaceDownPhase)}
              {visibleCards[i] && renderCard(visibleCards[i], 0, true)}
            </div>
          );
        })}
      </div>
    </section>
  );
}
